import { Checkbox, CheckIcon, Input } from '@bp/ui-components';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { SubstitutionDataHighlightEntry } from '../../../pages/Substitutions/Plan/PlanSubstitutions/PlanSubstitutions';
import { getMinutesDifference } from '../../../utils/dateCalculations';
import { lowercaseFirstLetter } from '../../../utils/stringHelper';
import { SubstitutionFormType } from './SubstitutionForm';
import styles from './SubstitutionFormRow.module.scss';

export type SubstitutionFormRowStatus =
  | 'normal'
  | 'attendance'
  | 'cancellation-school-event'
  | 'substitution'
  | 'on-school-event'
  | 'signed-out'
  | 'other';

export type SubstitutionFormRowEventType = {
  eventUuid: string;
  status: SubstitutionFormRowStatus;
  start: Dayjs;
  end: Dayjs;
  name: string;
  subjectShortName: string;
  subjectName: string;
  classOrGroupNames: string;
};

export type SubstitutionFormRowType = {
  teacherUuid: string;
  teacherName: string;
  isOriginalEventTeacher: boolean;
  selected: boolean;
  givenSubstituteHours: number | null;
  target: number;
  current: number;
  sameClass: boolean;
  sameSubject: boolean;
  events: SubstitutionFormRowEventType[];
  highlights: SubstitutionDataHighlightEntry[];
  disabled: boolean;
};

type SubstitutionFormRowProps = {
  hours: Dayjs[];
  hourWidth: number;
  frames: { left: number; width: number }[];
  row: SubstitutionFormRowType;
  index: number;
};

export const SubstitutionFormRow = ({ hours, hourWidth, frames, row, index }: SubstitutionFormRowProps) => {
  const { t } = useTranslation();

  const { setFieldValue, setFieldTouched } = useFormikContext<SubstitutionFormType>();

  const {
    teacherUuid,
    teacherName,
    selected,
    givenSubstituteHours,
    target,
    current,
    sameClass,
    sameSubject,
    events,
    highlights,
    disabled,
    isOriginalEventTeacher,
  } = row;

  return (
    <div
      className={classNames(styles['substitution-form-row'], {
        [styles.selected]: selected,
        [styles.disabled]: disabled,
      })}
    >
      <div className={styles.data}>
        <div className={styles.tiny}>
          <Checkbox
            checked={selected}
            name={`select-${teacherUuid}`}
            onChange={async (value) => {
              await setFieldValue(`substitutionRows[${index}].selected`, value.target.checked);
              if (!value.target.checked) {
                await setFieldValue(`substitutionRows[${index}].givenSubstituteHours`, null);
              } else {
                await setFieldValue(
                  `substitutionRows[${index}].givenSubstituteHours`,
                  isOriginalEventTeacher ? null : 1,
                );
              }
            }}
            disabled={disabled}
          />
        </div>
        <div className={styles.big} title={teacherName}>
          {teacherName}
        </div>
        <div className={styles.small}>
          {
            <Input
              type='number'
              dense
              name={`value-${teacherUuid}`}
              disabled={!selected}
              onChange={async (event) => {
                const value = Number(event.target.value);
                await setFieldTouched(`substitutionRows[${index}].givenSubstituteHours`, true);
                await setFieldValue(`substitutionRows[${index}].givenSubstituteHours`, value);
              }}
              value={givenSubstituteHours}
              className={styles['value-input']}
            />
          }
        </div>
        <div className={styles.tiny}>{target}</div>
        <div className={styles.tiny}>{current}</div>
        <div className={styles.medium}>{sameClass && <CheckIcon />}</div>
        <div className={styles.medium}>{sameSubject && <CheckIcon />}</div>
      </div>

      <div className={styles.hours} style={{ gridTemplateColumns: `repeat(${hours.length}, 1fr)` }}>
        {hours.map((hour) => {
          return <div key={hour.toString()} className={styles.hour}></div>;
        })}

        {hours.map((hour, hourIndex) => {
          return highlights
            .filter(({ fullDay, inTimegrid, renderStart }) => {
              return (fullDay || inTimegrid) && dayjs(renderStart).hour() === hour.hour();
            })
            .map(
              ({ highlightUuid: uuid, start, renderStart, end, renderEnd, status, highlightName, groupClassNames }) => {
                const difference = getMinutesDifference(hour, renderStart);
                const duration = getMinutesDifference(renderStart, renderEnd);

                const offset = Math.ceil((hourWidth / 60) * difference + hourIndex * hourWidth);
                const width = Math.ceil((hourWidth / 60) * duration);

                const startEndSameDay = dayjs(start).isSame(end, 'day');

                const format = startEndSameDay ? 'HH:mm' : 'DD.MM.YY, HH:mm';

                const title = groupClassNames
                  ? `${teacherName}\n${groupClassNames}\n${dayjs(start).format(format)} ${lowercaseFirstLetter(t('common.until'))} ${dayjs(end).format(format)} \n${highlightName}`
                  : `${teacherName}\n${dayjs(start).format(format)} ${lowercaseFirstLetter(t('common.until'))} ${dayjs(end).format(format)} \n${highlightName}`;

                return (
                  <div
                    key={`${uuid}-${teacherUuid}-${hour.toDate()}-${start.toString()}`}
                    className={classNames(styles.highlight, styles['highlight-status-' + status])}
                    style={{ left: `${offset}px`, width: `${width - 1}px` }}
                    title={title}
                  >
                    {highlightName && (
                      <div className={styles.name} title={title}>
                        {highlightName}
                      </div>
                    )}
                  </div>
                );
              },
            );
        })}

        {hours.map((hour, hourIndex) => {
          return events
            .filter((event) => {
              return dayjs(event.start).hour() === hour.hour();
            })
            .map(({ eventUuid: uuid, status, start, end, subjectName, subjectShortName, classOrGroupNames, name }) => {
              const difference = getMinutesDifference(hour, start);
              const duration = getMinutesDifference(start, end);

              const offset = Math.ceil((hourWidth / 60) * difference + hourIndex * hourWidth);
              const width = Math.ceil((hourWidth / 60) * duration);

              const title = `${subjectName}\n${classOrGroupNames}\n${teacherName}\n${name}`;

              return (
                <div
                  key={`${uuid}-${teacherUuid}-${hour.toDate()}-${start.toString()}`}
                  className={classNames(styles.event, styles['event-status-' + status])}
                  style={{ left: `${offset}px`, width: `${width - 1}px` }}
                  title={title}
                >
                  <div className={styles.name} title={title}>
                    {subjectShortName}
                  </div>
                </div>
              );
            });
        })}

        {frames.map(({ left, width }, index) => {
          return (
            <div
              key={left + width + index}
              className={styles.frame}
              style={{ left: `${left}px`, width: `${width}px` }}
            ></div>
          );
        })}
      </div>
    </div>
  );
};
